/**
 * roomStings.js
 * -------------
 * Hover-triggered synthesized ambient stings for each villa room. All audio
 * is generated live via Web Audio API — no files required. Each recipe
 * captures the "feel" of the room even if it's not literal foley.
 *
 * Usage:
 *   const engine = createRoomStingEngine();
 *   engine.play("basement");  // starts basement sting
 *   engine.stop();            // fades everything out
 *   engine.destroy();         // tears down AudioContext
 */

const STING_GAIN = 0.14;
const FADE_IN_MS = 300;
const FADE_OUT_MS = 500;

// Utility: creates a decorrelated pink-noise buffer of `sec` seconds
const makeNoiseBuffer = (ctx, sec) => {
  const buf = ctx.createBuffer(1, Math.max(1, Math.floor(ctx.sampleRate * sec)), ctx.sampleRate);
  const ch = buf.getChannelData(0);
  let b0 = 0, b1 = 0, b2 = 0;
  for (let i = 0; i < ch.length; i++) {
    const w = Math.random() * 2 - 1;
    b0 = 0.99 * b0 + w * 0.02;
    b1 = 0.95 * b1 + w * 0.05;
    b2 = 0.85 * b2 + w * 0.15;
    ch[i] = b0 + b1 + b2 + w * 0.1;
  }
  return buf;
};

// Utility: single short percussive noise burst through a bandpass — used as
// building block for clangs, ticks, drips, chimes.
const noisePluck = (ctx, dest, opts) => {
  const {
    freq = 800, q = 20, attack = 0.005, decay = 0.15, gain = 0.4, when = 0,
  } = opts;
  const t0 = ctx.currentTime + when;
  const src = ctx.createBufferSource();
  src.buffer = makeNoiseBuffer(ctx, 0.5);
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = freq;
  bp.Q.value = q;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + decay);
  src.connect(bp).connect(g).connect(dest);
  src.start(t0);
  src.stop(t0 + attack + decay + 0.05);
};

// Utility: sine ping (bell / chime)
const sinePing = (ctx, dest, opts) => {
  const { freq = 880, decay = 0.6, gain = 0.15, when = 0 } = opts;
  const t0 = ctx.currentTime + when;
  const osc = ctx.createOscillator();
  osc.frequency.value = freq;
  osc.type = "sine";
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + decay);
  osc.connect(g).connect(dest);
  osc.start(t0);
  osc.stop(t0 + decay + 0.05);
};

// ============================================================================
// Per-room sting recipes. Each takes (ctx, dest, cleanupList) and returns
// an object with a `.stop()` method that halts any long-running loops.
// ============================================================================
const RECIPES = {
  // Basement — slow low bass rumble + irregular heavy chain clanks
  basement: (ctx, dest, cleanups) => {
    // Deep sub rumble bed
    const rumble = ctx.createOscillator();
    rumble.type = "sawtooth";
    rumble.frequency.value = 48;
    const rlp = ctx.createBiquadFilter();
    rlp.type = "lowpass"; rlp.frequency.value = 120;
    const rg = ctx.createGain(); rg.gain.value = 0.06;
    rumble.connect(rlp).connect(rg).connect(dest);
    rumble.start();
    cleanups.push(() => { try { rumble.stop(); } catch { /* already stopped */ } });

    // Irregular heavy chain clank — bandpass noise pluck at low freq
    const scheduleClank = () => {
      noisePluck(ctx, dest, { freq: 220 + Math.random() * 180, q: 8, gain: 0.35, decay: 0.6 });
      noisePluck(ctx, dest, { freq: 90, q: 4, gain: 0.25, decay: 0.9, when: 0.04 });
      const t = window.setTimeout(scheduleClank, 900 + Math.random() * 1700);
      cleanups.push(() => window.clearTimeout(t));
    };
    scheduleClank();
  },

  // Bedroom — distant slow ocean surf + occasional nightingale whistle
  bedroom: (ctx, dest, cleanups) => {
    // Surf bed: low-passed pink noise with slow LFO breath
    const surf = ctx.createBufferSource();
    surf.buffer = makeNoiseBuffer(ctx, 3);
    surf.loop = true;
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass"; lp.frequency.value = 380; lp.Q.value = 0.5;
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.12;
    const lfoDepth = ctx.createGain();
    lfoDepth.gain.value = 80;
    lfo.connect(lfoDepth).connect(lp.frequency);
    lfo.start();
    const sg = ctx.createGain(); sg.gain.value = 0.55;
    surf.connect(lp).connect(sg).connect(dest);
    surf.start();
    cleanups.push(() => {
      try { surf.stop(); lfo.stop(); } catch { /* ok */ }
    });

    // Nightingale — a short warble of two quick sine pings
    const nightingale = () => {
      const base = 2200 + Math.random() * 400;
      sinePing(ctx, dest, { freq: base,        decay: 0.12, gain: 0.10 });
      sinePing(ctx, dest, { freq: base * 1.22, decay: 0.12, gain: 0.10, when: 0.14 });
      sinePing(ctx, dest, { freq: base * 0.95, decay: 0.18, gain: 0.09, when: 0.32 });
      const t = window.setTimeout(nightingale, 3200 + Math.random() * 4500);
      cleanups.push(() => window.clearTimeout(t));
    };
    const t0 = window.setTimeout(nightingale, 1400);
    cleanups.push(() => window.clearTimeout(t0));
  },

  // Apothecary — clinking crystal glass + slow bubbling flask
  apothecary: (ctx, dest, cleanups) => {
    // Bubbling — tight bandpass noise pops in the low-mid
    const bubble = () => {
      noisePluck(ctx, dest, { freq: 260 + Math.random() * 150, q: 25, gain: 0.22, decay: 0.05 });
      const t = window.setTimeout(bubble, 260 + Math.random() * 380);
      cleanups.push(() => window.clearTimeout(t));
    };
    bubble();

    // Glass clink — high-Q sine ping in an occasional cluster of 2-3
    const clink = () => {
      const base = 2600 + Math.random() * 900;
      sinePing(ctx, dest, { freq: base,        decay: 0.35, gain: 0.14 });
      if (Math.random() < 0.6)
        sinePing(ctx, dest, { freq: base * 1.5,  decay: 0.28, gain: 0.10, when: 0.09 });
      if (Math.random() < 0.3)
        sinePing(ctx, dest, { freq: base * 0.75, decay: 0.30, gain: 0.10, when: 0.18 });
      const t = window.setTimeout(clink, 1500 + Math.random() * 2500);
      cleanups.push(() => window.clearTimeout(t));
    };
    const t0 = window.setTimeout(clink, 700);
    cleanups.push(() => window.clearTimeout(t0));
  },

  // Salon — crackling fireplace + low warm sub hum
  salon: (ctx, dest, cleanups) => {
    // Sub hum
    const hum = ctx.createOscillator();
    hum.type = "sine"; hum.frequency.value = 120;
    const hg = ctx.createGain(); hg.gain.value = 0.04;
    hum.connect(hg).connect(dest);
    hum.start();
    cleanups.push(() => { try { hum.stop(); } catch { /* ok */ } });

    // Crackle — rapid tiny noise plucks at high freq
    const crackle = () => {
      const count = 3 + Math.floor(Math.random() * 4);
      for (let i = 0; i < count; i++) {
        noisePluck(ctx, dest, {
          freq: 4200 + Math.random() * 2500, q: 15,
          gain: 0.12 + Math.random() * 0.14,
          decay: 0.03 + Math.random() * 0.03,
          when: i * (0.03 + Math.random() * 0.05),
        });
      }
      const t = window.setTimeout(crackle, 200 + Math.random() * 550);
      cleanups.push(() => window.clearTimeout(t));
    };
    crackle();
  },

  // Parlor — shuffled cards + occasional dice rattle
  parlor: (ctx, dest, cleanups) => {
    // Rhythmic dry ticks — cards being shuffled
    const shuffle = () => {
      const count = 4 + Math.floor(Math.random() * 6);
      for (let i = 0; i < count; i++) {
        noisePluck(ctx, dest, {
          freq: 1800 + Math.random() * 900, q: 4,
          gain: 0.20, decay: 0.04, when: i * 0.055,
        });
      }
      const t = window.setTimeout(shuffle, 1800 + Math.random() * 2500);
      cleanups.push(() => window.clearTimeout(t));
    };
    shuffle();

    // Dice rattle — dense clump of low bandpass ticks
    const dice = () => {
      const n = 8 + Math.floor(Math.random() * 6);
      for (let i = 0; i < n; i++) {
        noisePluck(ctx, dest, {
          freq: 500 + Math.random() * 400, q: 6,
          gain: 0.18, decay: 0.06,
          when: i * (0.025 + Math.random() * 0.03),
        });
      }
      const t = window.setTimeout(dice, 4500 + Math.random() * 3500);
      cleanups.push(() => window.clearTimeout(t));
    };
    const t0 = window.setTimeout(dice, 2100);
    cleanups.push(() => window.clearTimeout(t0));
  },

  // Atelier — soft paper crinkle + occasional bright chime bell
  atelier: (ctx, dest, cleanups) => {
    // Paper crinkle — very short high-freq noise pops
    const crinkle = () => {
      const count = 5 + Math.floor(Math.random() * 6);
      for (let i = 0; i < count; i++) {
        noisePluck(ctx, dest, {
          freq: 3200 + Math.random() * 1800, q: 3,
          gain: 0.09, decay: 0.02,
          when: i * (0.02 + Math.random() * 0.04),
        });
      }
      const t = window.setTimeout(crinkle, 900 + Math.random() * 1600);
      cleanups.push(() => window.clearTimeout(t));
    };
    crinkle();

    // Bell chime — bright bell every few seconds
    const chime = () => {
      const base = 1760 * Math.pow(2, Math.random() * 0.3);
      sinePing(ctx, dest, { freq: base,       decay: 1.6, gain: 0.12 });
      sinePing(ctx, dest, { freq: base * 2,   decay: 1.3, gain: 0.06, when: 0.01 });
      sinePing(ctx, dest, { freq: base * 3.2, decay: 0.9, gain: 0.03, when: 0.02 });
      const t = window.setTimeout(chime, 5500 + Math.random() * 5000);
      cleanups.push(() => window.clearTimeout(t));
    };
    const t0 = window.setTimeout(chime, 2200);
    cleanups.push(() => window.clearTimeout(t0));
  },

  // Companion Wing — slow heartbeat pulse + very gentle breath
  companions: (ctx, dest, cleanups) => {
    const beat = () => {
      // Kick 1
      const osc1 = ctx.createOscillator();
      osc1.frequency.value = 68;
      const g1 = ctx.createGain();
      const t = ctx.currentTime;
      g1.gain.setValueAtTime(0.0001, t);
      g1.gain.exponentialRampToValueAtTime(0.5, t + 0.01);
      g1.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
      osc1.frequency.exponentialRampToValueAtTime(45, t + 0.18);
      osc1.connect(g1).connect(dest);
      osc1.start(t);
      osc1.stop(t + 0.22);
      // Kick 2
      const osc2 = ctx.createOscillator();
      osc2.frequency.value = 68;
      const g2 = ctx.createGain();
      const t2 = t + 0.28;
      g2.gain.setValueAtTime(0.0001, t2);
      g2.gain.exponentialRampToValueAtTime(0.35, t2 + 0.01);
      g2.gain.exponentialRampToValueAtTime(0.0001, t2 + 0.15);
      osc2.frequency.exponentialRampToValueAtTime(45, t2 + 0.15);
      osc2.connect(g2).connect(dest);
      osc2.start(t2);
      osc2.stop(t2 + 0.2);

      const nt = window.setTimeout(beat, 1150 + Math.random() * 250);
      cleanups.push(() => window.clearTimeout(nt));
    };
    beat();

    // Breath — slow filtered noise swell
    const breath = ctx.createBufferSource();
    breath.buffer = makeNoiseBuffer(ctx, 2);
    breath.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass"; bp.frequency.value = 400; bp.Q.value = 1.5;
    const bg = ctx.createGain(); bg.gain.value = 0.0001;
    breath.connect(bp).connect(bg).connect(dest);
    breath.start();
    // Slow swell LFO on the breath gain
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.28;   // one breath every ~3.5s
    const lfoDepth = ctx.createGain();
    lfoDepth.gain.value = 0.10;
    lfo.connect(lfoDepth).connect(bg.gain);
    lfo.start();
    cleanups.push(() => {
      try { breath.stop(); lfo.stop(); } catch { /* ok */ }
    });
  },

  // Rooftop — high starry-night wind + faraway cricket chorus + occasional
  // soft wine-glass clink. Meant to feel like being outdoors after dark.
  rooftop: (ctx, dest, cleanups) => {
    // Wind bed — filtered noise with a slow LFO on the filter cutoff
    const wind = ctx.createBufferSource();
    wind.buffer = makeNoiseBuffer(ctx, 3);
    wind.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass"; bp.frequency.value = 700; bp.Q.value = 1.2;
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.15;
    const lfoDepth = ctx.createGain(); lfoDepth.gain.value = 380;
    lfo.connect(lfoDepth).connect(bp.frequency);
    lfo.start();
    const wg = ctx.createGain(); wg.gain.value = 0.30;
    wind.connect(bp).connect(wg).connect(dest);
    wind.start();
    cleanups.push(() => { try { wind.stop(); lfo.stop(); } catch { /* ok */ } });

    // Cricket chorus — same bandpass noise triplets as the villa ambient,
    // but denser so it feels like being outside.
    const cricket = () => {
      noisePluck(ctx, dest, {
        freq: 4400 + Math.random() * 1200, q: 20,
        gain: 0.18, decay: 0.03,
      });
      noisePluck(ctx, dest, {
        freq: 4400 + Math.random() * 1200, q: 20,
        gain: 0.18, decay: 0.03, when: 0.05,
      });
      const t = window.setTimeout(cricket, 500 + Math.random() * 900);
      cleanups.push(() => window.clearTimeout(t));
    };
    cricket();

    // Occasional soft wine-glass clink
    const clink = () => {
      const base = 2200 + Math.random() * 700;
      sinePing(ctx, dest, { freq: base,       decay: 0.6, gain: 0.11 });
      sinePing(ctx, dest, { freq: base * 1.5, decay: 0.4, gain: 0.06, when: 0.03 });
      const t = window.setTimeout(clink, 5500 + Math.random() * 5000);
      cleanups.push(() => window.clearTimeout(t));
    };
    const t0 = window.setTimeout(clink, 3200);
    cleanups.push(() => window.clearTimeout(t0));
  },
};

export const createRoomStingEngine = () => {
  let ctx = null;
  let master = null;
  let currentCleanups = [];

  const ensureCtx = () => {
    if (ctx) return ctx;
    const A = window.AudioContext || window.webkitAudioContext;
    if (!A) return null;
    ctx = new A();
    master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);
    return ctx;
  };

  const clearCleanups = () => {
    while (currentCleanups.length) {
      const fn = currentCleanups.pop();
      try { fn(); } catch { /* fine */ }
    }
  };

  const play = (roomKey) => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem("rs_ambient_muted") === "1") return;
    const recipe = RECIPES[roomKey];
    if (!recipe) return;
    ensureCtx();
    if (!ctx) return;
    // Stop any previous sting first, then swing gain in
    stop(true);
    master.gain.cancelScheduledValues(ctx.currentTime);
    master.gain.setValueAtTime(master.gain.value || 0.0001, ctx.currentTime);
    master.gain.exponentialRampToValueAtTime(
      STING_GAIN, ctx.currentTime + FADE_IN_MS / 1000,
    );
    recipe(ctx, master, currentCleanups);
  };

  const stop = (silent = false) => {
    if (!ctx || !master) return;
    if (!silent) {
      master.gain.cancelScheduledValues(ctx.currentTime);
      master.gain.setValueAtTime(master.gain.value, ctx.currentTime);
      master.gain.exponentialRampToValueAtTime(
        0.0001, ctx.currentTime + FADE_OUT_MS / 1000,
      );
    }
    // Cleanups run after fade so we don't produce clicks
    const runCleanups = () => clearCleanups();
    window.setTimeout(runCleanups, silent ? 0 : FADE_OUT_MS + 20);
  };

  const destroy = () => {
    clearCleanups();
    if (ctx) {
      try { ctx.close(); } catch { /* ok */ }
    }
    ctx = null; master = null;
  };

  return { play, stop, destroy };
};
